import { PublicLinkPageStatus } from '@confluence/public-links/entry-points/PublicLinkPageQuery.ts';
import { expValEquals } from '@confluence/feature-experiments';

import type { CtaNotifyMessageProps } from '../controlled/shareAndRestrictDialog/cta/CtaNotifyMessage.tsx';
import type { DistributionFooterProps } from '../controlled/shareAndRestrictDialog/footer/DistributionFooter.tsx';
import type { FooterProps } from '../controlled/shareAndRestrictDialog/footer/Footer.tsx';
import type { MessageComposerProps } from '../controlled/shareAndRestrictDialog/footer/MessageComposer.tsx';

import type { StatesAndActions } from './Controller.tsx';
import { makePrincipalsMap } from './principalsMap.ts';

export function makeFooterProps(statesAndActions: StatesAndActions): FooterProps {
	const {
		derivedState: { natureOfCta, isPublishModalFlow },
	} = statesAndActions;
	const ctaNotifyMessageFooter =
		natureOfCta === 'none' ? undefined : makeCtaNotifyMessageProps(statesAndActions);

	return {
		ctaNotifyMessageFooter:
			!expValEquals('persistence_specific_access_gate', 'cohort', 'experiment') ||
			(ctaNotifyMessageFooter?.persistenceMode === 'staged' &&
				expValEquals('persistence_specific_access_gate', 'cohort', 'experiment'))
				? ctaNotifyMessageFooter
				: undefined,
		distributionFooter: isPublishModalFlow
			? undefined
			: makeDistributionFooterProps(statesAndActions),
	};
}

function isPublicLinkEditAllowedForContentType(statesAndActions: StatesAndActions): boolean {
	const { publicLinkPageData } = statesAndActions.queriesState;
	return publicLinkPageData.isLoading
		? false
		: publicLinkPageData.isPublicLinkEditAllowedForContentType;
}

function isPublicLinkOn(statesAndActions: StatesAndActions): boolean {
	const { publicLinkPageData } = statesAndActions.queriesState;
	if (
		!publicLinkPageData.isLoading &&
		publicLinkPageData.publicLinkStatus === PublicLinkPageStatus.ON
	) {
		return true;
	}
	return false;
}

function makeDistributionFooterProps(statesAndActions: StatesAndActions): DistributionFooterProps {
	const {
		actions: { openPublicLinkDialog, openShareToSlackDialog, clearElementToFocus },
		controllerProps: { contentId, contentType, contentSubType, pageMode, isUnpublishedDraft },
		queriesState: { spaceKey },
		derivedState: { isLockedOut, natureOfCta },
		controllerExtras: { publicLinkButtonRef, shareToSlackButtonRef },
	} = statesAndActions;

	return {
		contentId,
		contentType,
		spaceKey,
		pageMode,
		isLockedOut,
		isUnpublishedDraft,
		isLivePage: contentSubType === 'live',
		onSlackClick: openShareToSlackDialog,
		onPublicLinkClick: openPublicLinkDialog,
		isPublicLinkOn: isPublicLinkOn(statesAndActions),
		isPublicLinkEditAllowedForContentType: isPublicLinkEditAllowedForContentType(statesAndActions),
		lastTriggeredDialogVariant: statesAndActions.masterState.lastTriggeredDialogVariant,
		clearElementToFocus,
		publicLinkButtonRef,
		shareToSlackButtonRef,
		natureOfCta,
	};
}

function makeMessageComposerProps(
	statesAndActions: StatesAndActions,
	rows: number,
	notifyCheckbox: 'hidden' | 'checked' | 'unchecked',
): MessageComposerProps {
	const {
		derivedState: { isLockedOut },
		masterState: { optionalMessage },
		actions: { changeOptionalMessage },
	} = statesAndActions;

	return {
		isLockedOut,
		notifyMessage: optionalMessage,
		rows,
		isCheckboxUnchecked: notifyCheckbox === 'unchecked',
		onNotifyMessageChange: changeOptionalMessage,
	};
}

export function makeCtaNotifyMessageProps(
	statesAndActions: StatesAndActions,
): CtaNotifyMessageProps {
	const {
		masterState: {
			isSaving,
			notifyCheckedIfAvailable,
			showMessageComposerUnderCheckbox,
			isSpecificAccessExpanded,
			principalsStagerPrincipals,
		},
		derivedState: {
			isLockedOut,
			natureOfCta,
			isCheckboxVisible,
			areEmailsOnlyPresent,
			sharingType,
			specificAccessIds,
			exceededLimit,
			isSharingToEmailsDisabled,
			includeInvites,
			generalAccess,
			persistenceMode,
		},
		actions,
		controllerProps: { pageMode, isUnpublishedDraft, contentSubType, contentId },
	} = statesAndActions;

	const notifyCheckbox = (() => {
		if (
			persistenceMode === 'staged' &&
			expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
		) {
			return 'hidden';
		}

		if (!isCheckboxVisible) return 'hidden';
		return notifyCheckedIfAvailable ? 'checked' : 'unchecked';
	})();

	const shouldShowMessageComposer = (() => {
		if (
			persistenceMode === 'staged' &&
			expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
		) {
			return false;
		}

		if (
			natureOfCta === 'notify' &&
			persistenceMode === 'live' &&
			expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
		) {
			return true;
		}

		return (
			expValEquals('persistence_specific_access_gate', 'cohort', 'experiment') ||
			(notifyCheckbox === 'checked' && showMessageComposerUnderCheckbox)
		);
	})();

	const checkboxLabel = (() => {
		if (expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')) {
			return 'notify-them';
		}
		if (areEmailsOnlyPresent) return 'recipients';
		return 'newly-added';
	})();

	const isSpecificAccessDetailsVisible = specificAccessIds.length > 0 && isSpecificAccessExpanded;

	const hasGroups = principalsStagerPrincipals.some((p) => p.type === 'group');
	const hasTeams = principalsStagerPrincipals.some((p) => p.type === 'team');
	const hasUsers = principalsStagerPrincipals.some(
		(p) => p.type && ['user', 'guest', 'email'].includes(p.type),
	);

	const principalsCount = makePrincipalsMap(
		statesAndActions.masterState,
		statesAndActions.queriesState,
		{
			shouldIncludeUserPickerStagedAdditions: true,
			generalAccess,
		},
	).size;

	const messageComposerRows = expValEquals(
		'persistence_specific_access_gate',
		'cohort',
		'experiment',
	)
		? 5
		: 3;

	const shouldShowCtaButtons = () => {
		return (
			!expValEquals('persistence_specific_access_gate', 'cohort', 'experiment') ||
			(persistenceMode === 'staged' &&
				expValEquals('persistence_specific_access_gate', 'cohort', 'experiment'))
		);
	};

	return {
		persistenceMode,
		shouldShowCtaButtons: shouldShowCtaButtons(),
		natureOfCta,
		notifyCheckbox,
		checkboxLabel,
		isCtaInProgress: isSaving,
		messageComposer: shouldShowMessageComposer
			? makeMessageComposerProps(statesAndActions, messageComposerRows, notifyCheckbox)
			: undefined,
		sharingType,
		onCtaClick: actions.handleCtaClick,
		onCancelClick: actions.dismiss,
		isSpecificAccessDetailsVisible,
		onNotifyCheckboxClick: () => {
			actions.setNotifyChecked(!notifyCheckedIfAvailable);
		},
		onNotifyAddMessageClick: actions.toggleNotifyAddMessage,
		isLimitExceeded: exceededLimit.isLimitExceeded,
		pageMode,
		isDraft: isUnpublishedDraft,
		isLivePage: contentSubType === 'live',
		isSharingToEmailsDisabled,
		contentId,
		hasGroups,
		hasTeams,
		hasUsers,
		includeInvites,
		generalAccessLevel: generalAccess,
		principalsCount,
		isCheckboxDisabled: isLockedOut || isSharingToEmailsDisabled || natureOfCta === 'notify',
		isLockedOut,
	};
}
